import { Vector3, Vector2 } from "three";

export class SubMesh {
    public vertices: Vector3[];
    public uvs: Vector2[];
    public triangles: number[];

    constructor(vertices?: Vector3[], triangles?: number[], uvs?: Vector2[]) {
        this.vertices = vertices || [];
        this.triangles = triangles || [];
        this.uvs = uvs || [];
    }

    merge(vertices: Vector3[], triangles: number[], uvs?: Vector2[]) {
        const vertCount = this.vertices.length;
        this.vertices.push(...vertices);
        this.uvs.push(...(uvs || []));
        this.triangles.push(...triangles.map(i => i + vertCount));
    }

    mergeMesh(subMesh: SubMesh)
    {
        if (subMesh == null)
            return;

        this.merge(subMesh.vertices, subMesh.triangles, subMesh.uvs);
    }

    flip(): SubMesh {
        this.triangles = [...this.triangles];
        for (let i = 0; i < this.triangles.length; i += 3) {
            const temp = this.triangles[i];
            this.triangles[i] = this.triangles[i + 1];
            this.triangles[i + 1] = temp;
        }
        return this;
    }

    deconstruct(): [Vector3[], number[], Vector2[]] {
        return [this.vertices, this.triangles, this.uvs];
    }

    static box(min: Vector3, max: Vector3): SubMesh {
        const vertices = [
            // bottom
            new Vector3(min.x, min.y, min.z),
            new Vector3(max.x, min.y, min.z),
            new Vector3(max.x, min.y, max.z),
            new Vector3(min.x, min.y, max.z),

            // top
            new Vector3(min.x, max.y, max.z),
            new Vector3(max.x, max.y, max.z),
            new Vector3(max.x, max.y, min.z),
            new Vector3(min.x, max.y, min.z),

            // left
            new Vector3(min.x, min.y, max.z),
            new Vector3(min.x, max.y, max.z),
            new Vector3(min.x, max.y, min.z),
            new Vector3(min.x, min.y, min.z),

            // right
            new Vector3(max.x, min.y, min.z),
            new Vector3(max.x, max.y, min.z),
            new Vector3(max.x, max.y, max.z),
            new Vector3(max.x, min.y, max.z),

            // front
            new Vector3(max.x, min.y, max.z),
            new Vector3(max.x, max.y, max.z),
            new Vector3(min.x, max.y, max.z),
            new Vector3(min.x, min.y, max.z),

            // back
            new Vector3(min.x, min.y, min.z),
            new Vector3(min.x, max.y, min.z),
            new Vector3(max.x, max.y, min.z),
            new Vector3(max.x, min.y, min.z),
        ];

        const triangles = new Array<number>(vertices.length / 4)
            .fill(0)
            .map((_, i) => i * 4)
            .flatMap(i => [i, i + 1, i + 2, i, i + 2, i + 3]);

        return new SubMesh(vertices, triangles);
    }

    static polygon(...vertices: Vector3[]): SubMesh {
        if (vertices.length < 3) throw new Error();

        const triangles = Array.from({ length: vertices.length - 2 }, (_, i) => [0, i + 1, i + 2]).flat();

        return new SubMesh(vertices, triangles);
    }

    static outlineVertices(vertices: Vector3[]) {
        return this.outline(true, ...vertices);
    }

    static outline(loop: boolean, ...vertices: Vector3[]): SubMesh {
        if (vertices.length < 3) throw new Error();

        const polygonNormal = new Vector3().crossVectors(vertices[vertices.length - 1].sub(vertices[0]), vertices[1].sub(vertices[0]));

        const matchNormal = [true];

        for (let i = 1; i < vertices.length; ++i) {
            const cross = new Vector3().crossVectors(vertices[i - 1].sub(vertices[i]), vertices[(i + 1) % vertices.length].sub(vertices[i]));
            matchNormal.push(cross.lengthSq() < 0.00001 || cross.dot(polygonNormal) > 0);
        }

        if (matchNormal.filter(v => v).length <= vertices.length / 2) {
            for (let i = 0; i < vertices.length; ++i) matchNormal[i] = !matchNormal[i];
            polygonNormal.negate();
        }

        const triangles: number[] = [];

        const [begin, end] = loop ? [0, vertices.length] : [1, vertices.length - 1];

        for (let i = begin; i < end; i++) {
            if (matchNormal[i]) {
                triangles.push(i, (i + 1) % vertices.length, (vertices.length + i - 1) % vertices.length);
                triangles.push((vertices.length + i - 1) % vertices.length, (i + 1) % vertices.length);
            } else {
                triangles.push(i, (i + 1) % vertices.length, (vertices.length + i - 1) % vertices.length);
            }
        }

        return new SubMesh(vertices, triangles);
    }

    transform(transform: (v: Vector3) => Vector3): SubMesh {
        this.vertices.forEach((v, i) => {
            this.vertices[i] = transform(v);
        });
        return this;
    }

    clone(): SubMesh {
        return new SubMesh(this.vertices.slice(), this.triangles.slice(), this.uvs.slice());
    }

    empty(): boolean {
        return this.vertices.length === 0;
    }
}

